import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FaCheck, FaEye, FaInbox, FaTimes, FaUndo } from "react-icons/fa";
import api from "../../api";
import "./KpiEvidenceReviewPage.css";

// Phase 31. "KPI Evidence Review" — the authorized-reviewer counterpart to
// Developer/src/pages/developer/KpiEvidencePage.jsx. Never rendered inside
// Developer Home. Self-review is enforced server-side (services/
// kpiEvidenceReviewService.js) — the Approve/Reject/Request Changes actions
// simply won't be offered to a reviewer viewing their own submission
// (backend returns 403 regardless, this is UX only).
function StatusBadge({ status }) {
  const map = {
    SUBMITTED: { bg: "info", label: "Submitted" },
    UNDER_REVIEW: { bg: "info", label: "Under Review" },
    APPROVED: { bg: "success", label: "Approved" },
    NEEDS_CHANGES: { bg: "warning", text: "dark", label: "Changes Requested" },
    REJECTED: { bg: "danger", label: "Rejected" },
  };
  const cfg = map[status] || { bg: "secondary", label: status || "—" };
  return <Badge bg={cfg.bg} text={cfg.text}>{cfg.label}</Badge>;
}

function SourceBadge({ evidenceSource }) {
  if (evidenceSource === "FORM") return <Badge bg="info">Form</Badge>;
  if (evidenceSource === "MANUAL") return <Badge bg="warning" text="dark">Manual</Badge>;
  return <Badge bg="secondary">{evidenceSource || "—"}</Badge>;
}

// Phase 63: evidence prepared from the employee's "My KRA Work" records
// (details_json.source.type = ANALYST_WORK_RECORDS) shows those records and
// their attachments, so the reviewer checks the actual work, not just totals.
async function downloadSourceAttachment(attachment) {
  const res = await api.get(`/kpi-evidence-review/work-record-attachments/${attachment.id}/download`, { responseType: "blob" });
  const href = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = attachment.original_name || "attachment";
  a.click();
  window.URL.revokeObjectURL(href);
}

function SourceRecords({ evidenceId }) {
  const [records, setRecords] = useState(null);
  useEffect(() => {
    api.get(`/kpi-evidence-review/${evidenceId}/source-records`)
      .then((res) => setRecords(res?.data?.data || []))
      .catch(() => setRecords([]));
  }, [evidenceId]);
  if (!records) return <Spinner animation="border" size="sm" />;
  if (!records.length) return <div className="kerp-history-remarks">No linked work records.</div>;
  return (
    <div className="kerp-history">
      <h6>Source work records ({records.length})</h6>
      <Table size="sm" bordered responsive className="mb-0">
        <thead><tr><th>Record</th><th>Date</th><th>School</th><th>Status</th><th>Files</th></tr></thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id}>
              <td>{r.title}</td>
              <td>{r.record_date || r.due_date || r.request_at || r.period_month}</td>
              <td>{r.school_name || "—"}</td>
              <td>{r.status}</td>
              <td>
                {(r.attachments || []).map((a) => (
                  <button key={a.id} type="button" className="btn btn-link btn-sm p-0 d-block" onClick={() => downloadSourceAttachment(a)}>{a.original_name}</button>
                ))}
                {r.details?.evidence_link ? <a href={r.details.evidence_link} target="_blank" rel="noopener noreferrer">Link</a> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// Phase 67: reviewer manual rating for a judgement KPI (a KPI with NO rating
// thresholds, e.g. Data audit observations / Process automation
// initiatives). Shows the employee's factual count (month + YTD) exactly as
// their My Performance page computes it (GET .../kpi-score), and -- once the
// evidence is APPROVED -- lets the reviewer record a 1-5 rating with
// remarks (POST .../rating). Every rule (approved only, no-threshold KPI
// only, no self-rating, reviewer permission) is enforced server-side; this
// panel only mirrors it.
const RATING_OPTIONS = [1, 2, 3, 4, 5];

function formatValue(v) {
  if (v === null || v === undefined) return "—";
  return String(Math.round(Number(v) * 100) / 100);
}

export function ManualRatingPanel({ evidenceId, isSelf, onRated }) {
  const [score, setScore] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [rating, setRating] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  const load = useCallback(() => {
    api.get(`/kpi-evidence-review/${evidenceId}/kpi-score`)
      .then((res) => { setScore(res?.data?.data || null); setLoadError(""); })
      .catch((err) => setLoadError(err?.response?.data?.message || "Unable to load the KPI score."));
  }, [evidenceId]);

  useEffect(() => { load(); }, [load]);

  if (loadError) return <div className="kerp-history-remarks">{loadError}</div>;
  if (!score) return null;
  const kpi = score.kpi || {};
  if (!kpi.manual_rating_applicable) return null;

  const current = score.month_rating;
  const save = async () => {
    if (!rating) { setError("Select a rating from 1 to 5."); return; }
    if (!remarks.trim()) { setError("Remarks are required with a manual rating."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post(`/kpi-evidence-review/${evidenceId}/rating`, { rating: Number(rating), remarks: remarks.trim() });
      setSaved("Rating recorded.");
      setRating("");
      setRemarks("");
      load();
      onRated?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record the rating.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kerp-history kerp-manual-rating" data-testid="manual-rating-panel">
      <h6>Manual rating</h6>
      <div className="kerp-detail">
        <div><strong>This month:</strong> {formatValue(kpi.current_month?.value)}</div>
        <div><strong>Year to date:</strong> {formatValue(kpi.ytd?.value)}</div>
        <div>
          <strong>KPI rating:</strong>{" "}
          {kpi.rating === null || kpi.rating === undefined ? "Manual Rating Required" : `${kpi.rating} / 5`}
          {kpi.weighted_contribution !== null && kpi.weighted_contribution !== undefined ? ` (weighted ${formatValue(kpi.weighted_contribution)} of ${formatValue(kpi.weightage)})` : ""}
        </div>
        {current ? (
          <div data-testid="manual-rating-current">
            <strong>Rating for {score.period_month}:</strong> {current.rating_value} / 5 by {current.reviewed_by_name || "reviewer"}
            {current.reviewed_at ? ` on ${new Date(current.reviewed_at).toLocaleString()}` : ""}
            {current.remarks ? <div className="kerp-history-remarks">{current.remarks}</div> : null}
          </div>
        ) : null}
      </div>
      <p className="kerp-history-remarks">
        This KPI has no rating thresholds, so it is never rated automatically. The count above is the factual value; the rating is your judgement.
      </p>
      {!score.manual_rating_enabled ? (
        <div className="kerp-history-remarks">Manual rating is not enabled yet (database migration pending).</div>
      ) : isSelf ? (
        <div className="kerp-self-note">You cannot rate your own KPI.</div>
      ) : !score.can_rate ? (
        <div className="kerp-history-remarks">Approve this evidence first; the rating is given on approved evidence.</div>
      ) : (
        <div className="kerp-rating-form">
          <Form.Group className="mt-2">
            <Form.Label htmlFor={`manual-rating-${evidenceId}`}>{current ? "Change rating (1–5)" : "Rating (1–5)"}</Form.Label>
            <Form.Select id={`manual-rating-${evidenceId}`} size="sm" value={rating} onChange={(e) => setRating(e.target.value)}>
              <option value="">Select…</option>
              {RATING_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label htmlFor={`manual-rating-remarks-${evidenceId}`}>Reviewer remarks (required)</Form.Label>
            <Form.Control id={`manual-rating-remarks-${evidenceId}`} as="textarea" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Form.Group>
          <Button className="mt-2" size="sm" variant="primary" disabled={busy} onClick={save}>Save rating</Button>
        </div>
      )}
      {error ? <div className="kerp-error">{error}</div> : null}
      {saved ? <div className="kerp-history-remarks">{saved}</div> : null}
    </div>
  );
}

function DecisionModal({ evidence, currentUserId, onClose, onDecided }) {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!evidence) return;
    api.get(`/kpi-evidence-review/${evidence.evidence_id}`).then((res) => setDetail(res?.data?.data || null)).catch(() => setDetail(null));
    api.get(`/kpi-evidence-review/${evidence.evidence_id}/history`).then((res) => setHistory(res?.data?.data || [])).catch(() => setHistory([]));
  }, [evidence]);

  const isSelf = evidence && currentUserId != null && Number(evidence.submitted_by_id) === Number(currentUserId);

  const decide = async (action) => {
    if ((action === "reject" || action === "request_changes") && !remarks.trim()) {
      setError("Remarks are required for this decision.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(`/kpi-evidence-review/${evidence.evidence_id}/decision`, { action, remarks: remarks || null });
      onDecided?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record decision.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={!!evidence} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{evidence?.kpi_name} — {evidence?.submitted_by_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isSelf ? <div className="kerp-self-note">You submitted this evidence, so you cannot review or decide on it.</div> : null}
        {detail ? (
          <div className="kerp-detail">
            <div><strong>Period:</strong> {detail.period_month}</div>
            <div><strong>KRA:</strong> {evidence?.kra_name}</div>
            <pre className="kerp-details-json">{JSON.stringify(detail.details_json, null, 2)}</pre>
          </div>
        ) : <Spinner animation="border" size="sm" />}
        {detail?.details_json?.source?.type === "ANALYST_WORK_RECORDS" ? <SourceRecords evidenceId={evidence.evidence_id} /> : null}
        {evidence ? <ManualRatingPanel evidenceId={evidence.evidence_id} isSelf={isSelf} /> : null}

        {history.length ? (
          <div className="kerp-history">
            <h6>History</h6>
            <ul>
              {history.map((h) => (
                <li key={h.id}>
                  <strong>{h.event_type}</strong>{h.actor_name ? ` — ${h.actor_name}` : ""}
                  <span className="kerp-history-date">{new Date(h.created_at).toLocaleString()}</span>
                  {h.remarks ? <div className="kerp-history-remarks">{h.remarks}</div> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!isSelf ? (
          <Form.Group className="mt-3">
            <Form.Label>Remarks (required for Reject / Request Changes)</Form.Label>
            <Form.Control as="textarea" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Form.Group>
        ) : null}
        {error ? <div className="kerp-error">{error}</div> : null}
      </Modal.Body>
      {!isSelf ? (
        <Modal.Footer>
          <Button variant="outline-warning" disabled={busy} onClick={() => decide("request_changes")}><FaUndo /> Request Changes</Button>
          <Button variant="outline-danger" disabled={busy} onClick={() => decide("reject")}><FaTimes /> Reject</Button>
          <Button variant="success" disabled={busy} onClick={() => decide("approve")}><FaCheck /> Approve</Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
}

export default function KpiEvidenceReviewPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", evidence_source: "", period_month: "" });
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored?.id) setCurrentUserId(stored.id);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.evidence_source) params.evidence_source = filters.evidence_source;
      if (filters.period_month) params.period_month = filters.period_month;
      const res = await api.get("/kpi-evidence-review/queue", { params });
      setRows(res?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load the review queue.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="unit-page kerp-page">
      <div className="kerp-hero">
        <h2>KPI Evidence Review</h2>
        <p>Review and decide on Form/Manual KPI evidence submitted by employees.</p>
      </div>

      <div className="kerp-filters">
        <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="">All Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="CHANGES_REQUESTED">Changes Requested</option>
        </Form.Select>
        <Form.Select size="sm" value={filters.evidence_source} onChange={(e) => setFilters((f) => ({ ...f, evidence_source: e.target.value }))}>
          <option value="">All Sources</option>
          <option value="FORM">Form</option>
          <option value="MANUAL">Manual</option>
        </Form.Select>
        <Form.Control size="sm" type="month" value={filters.period_month} onChange={(e) => setFilters((f) => ({ ...f, period_month: e.target.value }))} />
      </div>

      {error ? <div className="kerp-error">{error}</div> : null}

      {loading ? (
        <div className="kerp-loading"><Spinner animation="border" /></div>
      ) : (
        <div className="unit-table-wrapper">
          <Table responsive className="unit-table tlp-table">
            <thead>
              <tr><th>Employee</th><th>KRA</th><th>KPI</th><th>Source</th><th>Period</th><th>Status</th><th className="text-center">Action</th></tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.review_stage_id}>
                  <td>{row.submitted_by_name}</td>
                  <td>{row.kra_name}</td>
                  <td>{row.kpi_name}</td>
                  <td><SourceBadge evidenceSource={row.evidence_source} /></td>
                  <td>{row.period_month}</td>
                  <td><StatusBadge status={row.evidence_status} /></td>
                  <td className="text-center">
                    <Button size="sm" variant="outline-primary" onClick={() => setSelectedEvidence(row)}><FaEye /> View</Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center kerp-empty-row"><FaInbox /> No evidence awaiting review.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <DecisionModal
        evidence={selectedEvidence}
        currentUserId={currentUserId}
        onClose={() => setSelectedEvidence(null)}
        onDecided={() => { setSelectedEvidence(null); load(); }}
      />
    </div>
  );
}
